import { TFunction } from 'i18next';
import { AiIndexingStateEnum } from 'graphql-common';
import { SupportiveMessageColor } from '@lib/components/SupportiveMessage/SupportiveMessage';

/**
 * The note under the indexing progress bar, from the server's own verdict.
 *
 * `indexingState` answers the question the five buckets cannot: whether anything
 * is still working on the documents that are not yet searchable. A document that
 * exhausts its retries never leaves `stale`/`failed`, and a failing re-embed now
 * pushes a frame just like a successful one — so neither the counts nor the
 * rhythm of the subscription distinguishes a draining backlog from an abandoned
 * one. **Never re-derive this from silence or from bucket movement.**
 */
export interface IndexingStatus {
  label: string;
  color: SupportiveMessageColor;
  icon: string;
  // Work is genuinely under way — the only state that earns a spinner.
  isWorking: boolean;
  // Whether `estimatedReindexSeconds` is worth rendering — see ETA_STATES.
  showsEta: boolean;
  // Whether the note is worth showing at all — see SILENT_STATES.
  showsMessage: boolean;
}

const LABEL_KEYS: Record<AiIndexingStateEnum, string> = {
  [AiIndexingStateEnum.UpToDate]: 'ai-indexing-state-up-to-date',
  [AiIndexingStateEnum.Working]: 'ai-indexing-state-working',
  [AiIndexingStateEnum.Scheduled]: 'ai-indexing-state-scheduled',
  [AiIndexingStateEnum.Deferred]: 'ai-indexing-state-deferred',
  [AiIndexingStateEnum.Blocked]: 'ai-indexing-state-blocked',
  [AiIndexingStateEnum.PausedBudget]: 'ai-indexing-state-paused-budget',
  [AiIndexingStateEnum.PausedProvider]: 'ai-indexing-state-paused-provider',
  [AiIndexingStateEnum.PausedPlan]: 'ai-indexing-state-paused-plan',
  [AiIndexingStateEnum.Stalled]: 'ai-indexing-state-stalled',
};

// Three tones only, and the difference between them is what the reader has to
// do: blue = the backlog is moving or will move on its own, yellow = a brake is
// holding it and someone decides when it lifts, red = it will not clear itself.
const APPEARANCE: Record<
  AiIndexingStateEnum,
  { color: SupportiveMessageColor; icon: string }
> = {
  [AiIndexingStateEnum.UpToDate]: {
    color: SupportiveMessageColor.Gray,
    icon: 'check_circle',
  },
  [AiIndexingStateEnum.Working]: {
    color: SupportiveMessageColor.Blue,
    icon: 'sync',
  },
  [AiIndexingStateEnum.Scheduled]: {
    color: SupportiveMessageColor.Blue,
    icon: 'schedule',
  },
  [AiIndexingStateEnum.Deferred]: {
    color: SupportiveMessageColor.Blue,
    icon: 'schedule',
  },
  [AiIndexingStateEnum.Blocked]: {
    color: SupportiveMessageColor.Red,
    icon: 'build',
  },
  [AiIndexingStateEnum.PausedBudget]: {
    color: SupportiveMessageColor.Yellow,
    icon: 'pause_circle',
  },
  [AiIndexingStateEnum.PausedProvider]: {
    color: SupportiveMessageColor.Yellow,
    icon: 'pause_circle',
  },
  [AiIndexingStateEnum.PausedPlan]: {
    color: SupportiveMessageColor.Yellow,
    icon: 'pause_circle',
  },
  [AiIndexingStateEnum.Stalled]: {
    color: SupportiveMessageColor.Red,
    icon: 'error',
  },
};

// The ETA is `retryingSoon × the region's per-document rate`, so the server
// returns a non-zero number in every state that has fast-lane work left —
// including STALLED (which is *defined* as fast-lane work that is not moving)
// and the PAUSED_* brakes. Rendering it there would promise "~1 min left"
// directly under "Not progressing — needs attention". A time remaining is a
// claim that the work is going to happen: only these two states make it.
const ETA_STATES: AiIndexingStateEnum[] = [
  AiIndexingStateEnum.Working,
  AiIndexingStateEnum.Scheduled,
];

// A banner is for what the reader has to act on or wait out, so only the brakes
// and the fault get one. Everything else is already legible from the row itself:
// the bar shows how far along it is, the spinning label icon shows work running,
// and the ETA beside the bar shows how much is left — a "Indexing…" banner under
// all three was a fourth way of saying the same thing. UP_TO_DATE and BLOCKED
// stay silent for their own reasons: the first is a full bar, and the second is
// meant to guarantee `parked > 0`, where the parked message says it with the
// breakdown and the retry attached — see the fallback below for when it does not.
const MESSAGE_STATES: AiIndexingStateEnum[] = [
  AiIndexingStateEnum.PausedBudget,
  AiIndexingStateEnum.PausedProvider,
  AiIndexingStateEnum.PausedPlan,
  AiIndexingStateEnum.Stalled,
];

export default function getIndexingStatus(
  state: AiIndexingStateEnum | null | undefined,
  parked: number | null | undefined,
  t: TFunction,
): IndexingStatus | null {
  if (!state) return null;
  const key = LABEL_KEYS[state];
  if (!key) return null;
  return {
    // BLOCKED is the one state whose copy names a number: what is left is
    // exactly the documents no automatic retry will touch again.
    label:
      state === AiIndexingStateEnum.Blocked
        ? t(key, { count: parked ?? 0 })
        : t(key),
    ...APPEARANCE[state],
    isWorking: state === AiIndexingStateEnum.Working,
    showsEta: ETA_STATES.includes(state),
    // BLOCKED hands its banner to the parked message, which is keyed off
    // `parked` — so a BLOCKED card reporting nothing parked would say nothing at
    // all, which is the one thing a red state must never do. Take the banner
    // back in that case; the copy's `=0` branch drops the count with it.
    showsMessage:
      MESSAGE_STATES.includes(state) ||
      (state === AiIndexingStateEnum.Blocked && !parked),
  };
}
